import { config } from '@static/js/env.config';
import queryString from 'query-string';
import { FormikHelpers, useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useRouteMatch } from '@shared/core';
import { Dimension, onSetDimensionOptionType, onSubmitType, ProductDetailsFields, UseBuyFormArgs } from './BuyForm.types';
import { useQueryParamHelper } from '@shared/core/queryString';
import { AddRegistryItemFromComponent, AddRegistryMethodType, ItemVariantSelectedProps } from '../../../../Catalog.telemetry.types';
import { DeriveTelemetryPageViewType, useDeriveTelemetryPage } from '../../../../utils/deriveTelemetryPage';
import { useCatalogContext } from '../../../../state/Catalog';
import { withWindow } from '@shared/utils/withWindow';
import { AddRegistryItemSource } from '../../../../Catalog.controller';
import { useMobileProductQuickAddDrawerProvider } from '@apps/registry/common/components/Catalog/components/ProductTile/components/MobileProductQuickAddDrawer';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { useCatalogLoggingContext } from '../../../../Catalog.logging';
import globalWindow from '@shared/core/globals';
import { generateRegistryItemMetadataSourceFromCatalog } from '../../../../utils/registryItemMetadata';

export const DIM_PREFIX = 'dim_';

const getTelemetryMethod = (isQuickAddForm: boolean | undefined, cashFundRelatedProduct: string | null): AddRegistryMethodType => {
  if (isQuickAddForm) return 'quick add';
  if (cashFundRelatedProduct) return 'related products quick add';
  return 'pdp cta';
};

const getTelemetryComponent = (): AddRegistryItemFromComponent['component'] => {
  if (globalWindow.location?.pathname.includes('shop/curations')) return 'expertCurationQuickAdd';
  return 'quickAdd';
};

const getTelemetryViewType = (isQuickAddForm: boolean | undefined): DeriveTelemetryPageViewType => {
  if (isQuickAddForm) return 'productGrid';
  return 'pdp';
};

const getTelemetryItemVariantMethod = (isQuickAddForm: boolean | undefined): ItemVariantSelectedProps['method'] => {
  if (isQuickAddForm) return 'grid';
  return 'pdp';
};

const getAddToRegistrySource = (isQuickAddForm: boolean | undefined): Exclude<AddRegistryItemSource, 'universalSearch'> => {
  if (isQuickAddForm) return 'catalog';
  return 'pdp';
};

export const useBuyForm = ({ product, isQuickAddForm, addCatalogItem, selectedProduct, setProductSelected, itemPosition }: UseBuyFormArgs) => {
  const { t: translateForm } = useTranslation('generalFormValidation');
  const adminRouteMatch = useRouteMatch<{ eventHandle: string; subRoute: string }>('/:eventHandle/edit/registry/:subRoute');
  const queryParams = useQueryParams();
  const { selectedCategory, goToCatalogItem } = useCatalogContext();
  const { trackBuyNowAction, trackItemVariantSelectAction } = useCatalogLoggingContext();
  const { getValueString } = useQueryParamHelper();
  const paramQty = getValueString('qty');
  const cashFundRelatedProduct = queryParams['relatedProductsQuickAdd'] as string | null;
  const comesFromChecklist = queryParams['source'] === 'checklist';
  const comesFromProductRec = queryParams['source'] === 'productRec';
  const comesFromSubcategorySpotlight = queryParams['source'] === 'subcategorySpotlight';

  const addToRegistrySource = getAddToRegistrySource(isQuickAddForm);

  const deriveTelemetryPage = useDeriveTelemetryPage();
  const telemetryPageValue = deriveTelemetryPage({
    selectedCategory,
    product: product,
    view: getTelemetryViewType(isQuickAddForm)
  });

  const { closeDrawer } = useMobileProductQuickAddDrawerProvider();

  const handleAddCatalogItem = useEventCallback<React.MouseEventHandler<HTMLButtonElement>>(() => {
    if (!formik.isSubmitting) {
      formik.submitForm();
      closeDrawer();
    }
  });

  const { dimensions } = useMemo(() => {
    let dimensions: Dimension[] = [];
    if (product && product.group?.groupDimensions) {
      const items = product.group.groupDimensions.map(groupDimension => {
        const foundItem = product.groupDimensionOptions.find(g => groupDimension.options.find(option => option.displayString === g.displayString));
        if (foundItem) {
          return {
            defaultValue: {
              label: foundItem.displayString,
              value: foundItem.id,
              color: foundItem.swatchColor?.hex,
              id: foundItem.id,
              imageUrl: foundItem.swatchUrl
            },
            id: groupDimension.id,
            label: groupDimension.title,
            options: groupDimension.options.map(option => {
              return {
                label: option.displayString,
                value: option.id,
                color: option.swatchColor?.hex,
                id: option.id,
                imageUrl: option.swatchUrl
              };
            }),
            swatchType: foundItem.swatchType
          };
        }
        return null;
      });
      dimensions = items.filter(item => item !== null) as Dimension[];
    }

    return {
      dimensions
    };
  }, [product]);

  // only run once on mount to check params and potentially auto add item
  useEffect(() => {
    if (paramQty && !isQuickAddForm) {
      formik.setFieldValue('qty', +paramQty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSetDimensionOption = useEventCallback<onSetDimensionOptionType>((catalogItem, groupDimensionTitle, groupDimensionOption, dimensionType, dimensionStringValue) => {
    trackItemVariantSelectAction({
      product: catalogItem,
      groupDimensionTitle,
      groupDimensionOption,
      method: getTelemetryItemVariantMethod(isQuickAddForm),
      dimensionType,
      dimensionStringValue,
      page: telemetryPageValue
    });

    if (setProductSelected) {
      setProductSelected(catalogItem);
    }

    if (!isQuickAddForm) {
      goToCatalogItem(catalogItem.id, 'push', window.location.search);
    }
  });

  const onSubmitBuyForm = useEventCallback<onSubmitType>((selectedProduct, productDetailsFields, actions) => {
    const method: AddRegistryMethodType = getTelemetryMethod(isQuickAddForm, cashFundRelatedProduct);

    const metadataSource = generateRegistryItemMetadataSourceFromCatalog(method, telemetryPageValue);
    addCatalogItem?.({
      source: addToRegistrySource,
      payload: {
        qty: +productDetailsFields.qty,
        product: selectedProduct,
        itemPosition,
        metadata: {
          source: metadataSource,
          sourceMetadata: {
            scannedPrice: selectedProduct.price?.floatingPointDecimalString
          }
        },
        telemetryArgs: {
          method,
          page: telemetryPageValue,
          productCatalogCategoryTypeOverride: adminRouteMatch?.params.subRoute === 'build' ? 'subcategory' : undefined,
          ...(cashFundRelatedProduct && { cashFundRelatedProduct }),
          ...(comesFromChecklist && { source: 'checklist', checklist: 'registry' }),
          ...(comesFromProductRec && { component: 'productRec', source: 'Overview Product Recommendations' }),
          ...(comesFromSubcategorySpotlight && { component: 'subcategorySpotlight' }),
          ...(isQuickAddForm && { component: getTelemetryComponent() })
        }
      }
    });

    // actions.resetForm();
  });

  const buyProductNow = useEventCallback<React.MouseEventHandler>(e => {
    e.preventDefault();
    if (product) {
      const path = queryString.stringifyUrl({
        url: `${config.redirectServiceUri}/redirect`,
        query: {
          url: product.productUrl || '',
          platform: 'web'
        }
      });
      trackBuyNowAction({ page: telemetryPageValue, product, action: 'redirect', method: 'pdp cta' });
      withWindow(global => {
        global.open(path, '_blank');
      });
    }
  });

  const handleSubmit = useEventCallback(async (productDetailsFields: ProductDetailsFields, actions: FormikHelpers<ProductDetailsFields>) => {
    if (selectedProduct) {
      onSubmitBuyForm(selectedProduct, productDetailsFields, actions);
    }
  });

  // Get the initial values for the form
  const dimFields = useMemo(
    () =>
      dimensions.reduce((acc: Record<string, string | undefined>, dimension) => {
        const dimensionKey: string = `${DIM_PREFIX}${dimension.id}`;
        acc[dimensionKey] = dimension.defaultValue?.id;
        return acc;
      }, {}),
    [dimensions]
  );
  const formik = useFormik<ProductDetailsFields>({
    initialValues: {
      qty: 1,
      ...dimFields
    },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: Yup.object<ProductDetailsFields>({
      qty: Yup.number().integer().required(translateForm('required')).min(1, 'Min is 1')
    })
  });
  const { setValues } = formik;

  // Set the initial values for product availability
  const lastUpdatedFieldOption = useRef<string>(dimensions[0]?.defaultValue?.id ?? '');

  // Handle all dimension changes
  const handleOnDimensionOptionChange = useEventCallback((optionValue: string, updatedField: string) => {
    if (!optionValue || !product?.group?.items) return;

    const dimensionKey = `${DIM_PREFIX}${updatedField}`;
    lastUpdatedFieldOption.current = optionValue;

    // Loop through the items to find a match
    let foundItem = product.group.items.find(item => {
      // Loop through the form values
      for (const key in formik.values) {
        // Skip non dimension fields
        if (!key.startsWith(DIM_PREFIX)) continue;
        // Use updated value if it matches the dimension type
        const valueToFind = key !== dimensionKey ? formik.values[key as keyof ProductDetailsFields] : optionValue;
        // Check if the item has the same dimension value
        // (this iteration could also be avoided if the dimensions are sorted unambiguously by using the array index)
        const foundDimension = item.groupDimensionOptions.some(dimension => dimension.id === valueToFind);
        if (!foundDimension) {
          return false;
        }
      }
      // Return true if all dimensions are found
      return true;
    });

    // If item is not found, find the next one that matches the last selected option
    if (!foundItem) {
      foundItem = product.group.items.find(item => {
        return item.groupDimensionOptions.some(dimension => dimension.id === lastUpdatedFieldOption.current);
      });
    }

    // If item is not found, find the next one that matches the last selected option
    if (foundItem) {
      // Update the selected item and tracking data
      const catalogItem = {
        ...foundItem,
        storefrontInfo: product.storefrontInfo,
        group: product.group,
        pdpSections: product.pdpSections,
        mediaAssets: product.mediaAssets
      };
      const catalogItemDimension = catalogItem.groupDimensionOptions.find(dimension => dimension.id === lastUpdatedFieldOption.current);
      const catalogItemDimensionTitle = catalogItemDimension ? catalogItemDimension.groupDimension.title : '';
      const catalogItemDimensionType = !catalogItemDimension || catalogItemDimension.swatchType === 'none' ? 'dimension' : catalogItemDimension.swatchType;
      const catalogItemDimensionColor = catalogItemDimension?.swatchType === 'color' ? catalogItemDimension.displayString : null;
      onSetDimensionOption(catalogItem, catalogItemDimensionTitle, catalogItem.title, catalogItemDimensionType, catalogItemDimensionColor);

      // Set form values with the alternative item dimension values
      const newValues = foundItem?.groupDimensionOptions.reduce((acc: Record<string, string>, dimension) => {
        const dimensionKey = `${DIM_PREFIX}${dimension.groupDimension.id}`;
        acc[dimensionKey] = dimension.id;
        return acc;
      }, {});

      // Set all dimension values
      setValues({ ...formik.values, ...newValues });
    }
  });

  const handleOnDimensionOptionChangeOnHover = useEventCallback((optionValue: string, updatedField: string) => {
    if (!optionValue || !product?.group?.items) {
      return;
    }

    const dimensionKey = `${DIM_PREFIX}${updatedField}`;
    lastUpdatedFieldOption.current = optionValue;

    const values = Object.keys(formik.values)
      .filter(i => i.startsWith(DIM_PREFIX))
      .map(i => (i === dimensionKey ? optionValue : formik.values[i as keyof ProductDetailsFields]));

    const foundItem =
      product.group.items.find(item => {
        return !values.some(value => !item.groupDimensionOptions.some(dimension => dimension.id === value));
      }) ||
      product.group.items.find(item => {
        return item.groupDimensionOptions.some(dimension => dimension.id === optionValue);
      });

    return foundItem?.id;
  });

  const dimensionFormValues = useMemo(() => {
    return Object.keys(formik.values).reduce((acc: Record<string, string | number | undefined>, key) => {
      if (key.startsWith(DIM_PREFIX)) {
        acc[key.substring(DIM_PREFIX.length)] = formik.values[key as keyof ProductDetailsFields];
      }
      return acc;
    }, {});
  }, [formik.values]);

  return {
    buyProductNow,
    dimensions,
    handleAddCatalogItem,
    formik,
    dimensionFormValues,
    setDimensionOption: handleOnDimensionOptionChange,
    handleOnDimensionOptionChangeOnHover
  } as const;
};
